import React, { useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import Scrollable from "../Shared/Edit/Scrollable";
import ErrorMessage from "../Shared/Error/ErrorMessage";
import { OverlayLoader } from "../Shared/Loader/OverlayLoader";
import InnerLoader from "../Shared/Loader/InnerLoader";
import { useTranslation } from "react-i18next";
import { updateSharedLookup } from "../../API/SharedLookup/SharedLookup";
import GenericTranslationDialog from "../Entities/GenericTranslationDialog";
import { isReservedAttributeName } from "./reservedAttributeNames";

const EditSharedPropertyDialog = ({ handleClose, sharedPropertyId, sharedProperties, updatedSharedProperties, project }) => {

    const { t, ready: tReady } = useTranslation([
        "attribute/shared_property",
    ]);

    const sharedProperty = sharedProperties.find((item) => item._id === sharedPropertyId);

    const [formField, setFormFields] = useState({ ...sharedProperty });
    const [showLabelTranslationDialog, setShowLabelTranslationDialog] = useState(false);

    const [validName, setValidName] = useState({
        isValid: true,
        errorMessage: ""
    });

    const [updatingSharedProperty, setUpdatingSharedProperty] = useState(false);

    const shouldDisableUpdateButton = () => {
        const isLanguagesUpdated = isLanguagesChanged(formField.languages, sharedProperty.languages);
        if (isLanguagesUpdated) {
            return false;
        }
        return !validName.isValid || formField.name.trim() === "" || isNameIsSameAsCurrent(formField.name) || isLanguagesUpdated;
    }

    const isNameIsSameAsCurrent = (name) => {
        return name === sharedProperty.name;
    }

    const handleNameChange = (e) => {
        const name = e.target.value.trim();
        const nameIsReserved = isReservedAttributeName(e.target.value);
        const nameIsBlank = name === "";
        const nameInUse = sharedProperties.some(item => item.name.trim().toLowerCase() === name.toLowerCase());
        const nameContainsSys = /^\[SYS\]/i.test(name);
        const nameStartWithNumber = /^\d/.test(name);
        const isNameLong = name.length > 30;
        const nameIsSameAsCurrent = isNameIsSameAsCurrent(name);

        if (nameIsSameAsCurrent) {
            setValidName({
                isValid: true,
                errorMessage: ""
            });
            setFormFields({ ...formField, name: e.target.value });
            return;
        }

        const validName = {
            isValid: !(nameIsBlank || nameStartWithNumber || nameInUse || isNameLong || nameContainsSys || nameIsReserved),
            errorMessage: ""
        }

        if (!validName.isValid) {
            if (nameIsBlank) {
                validName.errorMessage = t("edit_dialog.errors.name_required");
            } else if (nameStartWithNumber) {
                validName.errorMessage = t("edit_dialog.errors.name_starts_with_number");
            } else if (nameInUse) {
                validName.errorMessage = t("edit_dialog.errors.name_in_use");
            } else if (isNameLong) {
                validName.errorMessage = t("edit_dialog.errors.name_too_long");
            } else if (nameContainsSys) {
                validName.errorMessage = t("edit_dialog.errors.name_contains_sys");
            } else if (nameIsReserved) {
                validName.errorMessage = t("edit_dialog.errors.name_is_reserved", {}, "Name reserved. Try a different one.");
            } else {
                validName.errorMessage = "";
            }
        }
        setValidName(validName);
        setFormFields({ ...formField, name: e.target.value });
    }

    const setLabelTranslation = (translations) => {
        setFormFields({
            ...formField,
            languages: translations
        });
        setShowLabelTranslationDialog(false);
    }

    function isLanguagesChanged(languages, defaultLanguages) {
        const keys = Object.keys(languages);
        for (const key of keys) {
            if (
                languages[key].label !== defaultLanguages[key].label
            ) {
                return true;
            }
        }
        return false;
    }

    const updateSharedProperty = async () => {
        try {
            setUpdatingSharedProperty(true);
            const isNameChanged = !isNameIsSameAsCurrent(formField.name);
            const response = await updateSharedLookup(formField._id, formField.projectId, { name: formField.name, isNameChanged: isNameChanged, languages: formField.languages });
            const modifiedData = sharedProperties.map((item) => {
                if (item._id === formField._id) {
                    return {
                        ...item,
                        ...response.data
                    };
                }
                return item;
            });
            updatedSharedProperties(modifiedData, formField._id);
            handleClose()
        } catch (error) {
            console.error("Error occurred:", error);
        } finally {
            setUpdatingSharedProperty(false);
        }
    }

    return (
        <Dialog
            title={t("edit_dialog.title")}
            onClose={() => handleClose()}
            width="30%"
            height="50%"
        >
            {updatingSharedProperty && <OverlayLoader />}
            {showLabelTranslationDialog && (
                <GenericTranslationDialog
                    onClose={() => {
                        setShowLabelTranslationDialog(false);
                    }}
                    onSave={setLabelTranslation}
                    languages={formField.languages}
                    project={project}
                    fields={["label"]}
                    type={t("label")}
                />
            )}
            <form className="d-flex flex-column h-100" >
                <Scrollable>
                    <div className="mb-2">
                        <div className="me-1 form-label d-inline-block" required>
                            {t("edit_dialog.name_label")}
                        </div>
                        {!validName.isValid &&
                            <ErrorMessage
                                valid={validName.isValid}
                                error={validName.errorMessage}
                            />
                        }
                        <input
                            maxLength={64}
                            onChange={handleNameChange}
                            className="form-control name-text"
                            autoFocus={true}
                            type="text"
                            value={formField.name}
                        />
                    </div>
                    <div className="mt-3 cursor-pointer" onClick={() => {
                        setShowLabelTranslationDialog(true);
                    }}>
                        <span className="text-primary">
                            {t("label_translation")}
                        </span>
                    </div>
                </Scrollable>
            </form>
            <DialogActionsBar layout="end">
                <div className="m-3">
                    {updatingSharedProperty && (
                        <small className="mt-2">
                            <div className="d-inline-block">
                                <InnerLoader borderColor="green" />
                                <span className="me-4 text-danger"></span>
                            </div>
                        </small>
                    )}
                    <Button
                        onClick={() => handleClose()}
                        className="btn btn-secondary"
                    >
                        {t("edit_dialog.buttons.cancel_button_label")}
                    </Button>
                    <Button
                        disabled={shouldDisableUpdateButton()}
                        onClick={updateSharedProperty}
                        className="btn btn-primary"
                    >
                        {t("edit_dialog.buttons.update_button_label")}
                    </Button>
                </div>
            </DialogActionsBar>
        </Dialog >
    );
}
export default EditSharedPropertyDialog;
